import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import dompurify from "dompurify";
import { SearchJurisdictionModal } from "@components/Modals";
import { JurisdictionService } from "@services";
import utils from "@utils";
import {
	Switch,
	SearchField,
	ScrollDetect,
	ConfirmationModal,
	CreateJurisdictionModal,
} from "../../../components";

import { JurisdictionConflictModal } from "../components";

import "./JurisdictionList.scss";

const displayCount = 25;
let cancel;
const getCancelToken = c => {
	cancel = c;
};

class JurisdictionList extends Component {
	constructor(props) {
		super(props);

		this.state = {
			jurisdictions: [],
			pageNumber: 1,
			hasMore: true,
			searchValue: "",
			isActive: true,
			showJurisdictionModal: false,
			jurisdictionId: "",
			showJurisdictionConflictModal: false,
			conflictModalMessage: "",
			modalCancel: () => {},
			modalConfirm: () => {},
		};
	}

	componentWillUnmount() {
		if (cancel) cancel();
	}

	/**
	 * Update state to indicate that an action is currently being performed on a jurisdiction record
	 * @param {Number} jurisdictionId The ID number for the jurisdiction
	 * @param {Boolean} value Whether to mark the jurisdiction as loading or not. Defaults to `true`
	 */
	setJurisdictionLoading = (jurisdictionId, value = true) => {
		this.setState(({ jurisdictions }) => ({
			jurisdictions: jurisdictions.map(item =>
				item.Id === jurisdictionId ? { ...item, loading: value } : item,
			),
		}));
	};

	/** Retrieves a paginated list of jurisdictions for the current user */
	getJurisdictions = () => {
		const { lang } = global;
		const { pageNumber, searchValue, isActive } = this.state;

		if (cancel) cancel();
		JurisdictionService.getAll({
			getCancelToken,
			pageNumber,
			displayCount,
			isActive,
			searchValue,
		})
			.then(({ data }) => {
				cancel = undefined;
				const newData = data?.ItemList ?? [];

				this.setState(prevState => ({
					jurisdictions:
						prevState.pageNumber > 1
							? [...prevState.jurisdictions, ...newData]
							: newData,
					hasMore: newData.length >= displayCount,
					pageNumber: prevState.pageNumber + 1,
				}));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					const { response } = error;
					this.setState({ hasMore: false });

					utils.notify({
						type: "error",
						message: response?.data?.Message ?? lang.t("Error loading jurisdictions"),
					});
				}
			});
	};

	/**
	 * Makes an API request to mark a jurisdiction as active or inactive and updates the UI when successful
	 * @param {Number} jurisdictionId The ID number for the jurisdiction
	 * @returns {(event:Event) => void}
	 */
	toggleActiveStatus = jurisdictionId => event => {
		if (event) event.stopPropagation();

		const { lang } = global;

		JurisdictionService.updateJurisdictionApproval(jurisdictionId)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully updated"),
				});

				this.setState(({ jurisdictions, isActive }) => ({
					jurisdictions: jurisdictions
						.map(item =>
							item.Id === jurisdictionId
								? { ...item, IsActive: !item.IsActive }
								: item,
						)
						.filter(item => item.IsActive === isActive),
				}));
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error updating the jurisdiction"),
				});
			});
	};

	/**
	 * Migrates one or more officers from one jurisdiction to another
	 * @param {Number} jurisdictionId The ID number of the current jurisdiction officers belong to
	 * @param {Number} moveToJurisdictionId The ID number of the jurisdiction officers should be migrated to
	 */
	moveJurisdictionOfficers = (jurisdictionId, moveToJurisdictionId) => {
		const { lang } = global;

		JurisdictionService.moveJurisdiction(jurisdictionId, moveToJurisdictionId)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully moved and deleted"),
				});

				this.setState(state => ({
					jurisdictions: state.jurisdictions.filter(item => item.Id !== jurisdictionId),
				}));
			})
			.catch(() => {
				this.setJurisdictionLoading(jurisdictionId, false);
				utils.notify({
					type: "error",
					message: lang.t("Error moving the Jurisdiction"),
				});
			});
	};

	/**
	 * Makes a request to delete a jurisdiction record from RollKall. If the jurisdiction has at least one officer associated with it,
	 * A confirmation modal appears asking the user to migrate the officer(s) to another jurisdiction (see {@link moveJurisdictionOfficers})
	 * @param {Number} jurisdictionId The ID number of the jurisdiction
	 */
	deleteJurisdiction = jurisdictionId => {
		const { lang } = global;
		const sanitizer = dompurify.sanitize;
		this.setJurisdictionLoading(jurisdictionId);
		JurisdictionService.deleteJurisdiction(jurisdictionId)
			.then(() => {
				// TODO fix issue that hides the first item of the next page when deleting an item of the current page
				this.setState(state => ({
					jurisdictions: state.jurisdictions.filter(item => item.Id !== jurisdictionId),
				}));

				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully deleted"),
					title: lang.t("Success"),
				});
			})
			.catch(({ response }) => {
				// If status = 409, the jurisdiction has officers associated with it
				if (response.status === 409) {
					this.setState({
						showJurisdictionConflictModal: true,
						conflictModalMessage: (
							<span dangerouslySetInnerHTML={{ __html: sanitizer(response.data?.Message) }} />
						),
						modalCancel: () => {
							this.setState({
								showJurisdictionConflictModal: false,
								conflictModalMessage: "",
							});
							this.setJurisdictionLoading(jurisdictionId, false);
						},
						modalConfirm: () => {
							this.setState({
								showJurisdictionConflictModal: false,
								conflictModalMessage: "",
							});
							this._searchJurisdictionModal.openModal({
								title: lang.t("Search Jurisdictions"),
								confirmationButton: `${lang.t("Move")} ${lang.t("Officers")}`,
								text: lang.t("Search for a jurisdiction to move the officers into"),
								onCancel: () => this.setJurisdictionLoading(jurisdictionId, false),
								actionCallback: moveToJurisdictionId =>
									this.moveJurisdictionOfficers(
										jurisdictionId,
										moveToJurisdictionId,
									),
							});
						},
					});
				} else {
					this.setJurisdictionLoading(jurisdictionId, false);
					utils.notify({
						type: "error",
						message: lang.t("Error deleting jurisdiction"),
					});
				}
			});
	};

	/**
	 * Calls {@link onEditJurisdiction} if the 'Enter' key was pressed while focused on a jurisdiction row
	 * @param {Number} jurisdictionId The ID number of the jurisdiction
	 * @returns {(event:Event) => void}
	 */
	onRowKeyPress = jurisdictionId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;
		if (!preventNavigation && event.key === "Enter") this.onEditJurisdiction(jurisdictionId)();
	};

	/**
	 * Navigates the user to the jurisdiction form to edit the selected jurisdiction
	 * @param {Number} jurisdictionId The ID number of the jurisdiction
	 * @returns {(event:Event) => void}
	 */
	onEditJurisdiction = jurisdictionId => event => {
		if (event) event.stopPropagation();

		const { jurisdictions } = this.state;
		const jurisdictionItem = jurisdictions.find(item => item.Id === jurisdictionId);

		if (!jurisdictionItem.loading) {
			const { router } = this.props;
			router.push(`/jurisdictions/${jurisdictionId}`);
		}
	};

	/**
	 * Confirms the user's intention to delete a jurisdiction record. Calls {@link deleteJurisdiction} when confirmed.
	 * @param {Number} jurisdictionId The ID number of the jurisdiction
	 * @returns {(event:Event) => void}
	 */
	onDeleteJurisdiction = jurisdictionId => event => {
		if (event) event.stopPropagation();

		const { lang } = global;
		this._confirmationModal.openModal({
			title: lang.t("Delete Jurisdiction"),
			message: lang.t("Are you sure you want to delete this jurisdiction_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Jurisdiction")}`,
			onConfirm: () => this.deleteJurisdiction(jurisdictionId),
		});
	};

	/** Navigates the user to the jurisdiction form to create a new jurisdiction */
	onAddJurisdiction = () => {
		const { router } = this.props;
		router.push("/jurisdictions/add");
	};

	openJurisdictionModal = jurisdictionId =>
		this.setState({ showJurisdictionModal: true, jurisdictionId });

	closeJurisdictionModal = () =>
		this.setState({ showJurisdictionModal: false, jurisdictionId: "" });

	/**
	 * Updates the text used to filter jurisdictions by
	 * @param {String} searchValue The value to filter jurisdictions by
	 */
	onSearchJurisdiction = searchValue => {
		const { searchValue: currentSearch } = this.state;
		if (currentSearch !== searchValue) {
			this.setState({
				hasMore: true,
				jurisdictions: [],
				pageNumber: 1,
				searchValue,
			});
		}
	};

	onDisplayInactives = () => {
		this.setState(
			state => ({
				hasMore: true,
				jurisdictions: [],
				pageNumber: 1,
				isActive: !state.isActive,
			}),
			() => {
				// If ScrollDetect is already visible in the viewport, request data immediately
				this.getJurisdictions();
			},
		);
	};

	// Callback after sucessfully editing/creating a jurisdiction
	onSuccessCallback = () => {
		this.closeJurisdictionModal();
		// reset page number to intial state before calling jurisdictions
		this.setState({ pageNumber: 1 }, () => this.getJurisdictions());
	};

	render() {
		const { lang } = global;
		const {
			jurisdictions,
			hasMore,
			isActive,
			showJurisdictionModal,
			jurisdictionId,
			showJurisdictionConflictModal,
			conflictModalMessage,
			modalCancel,
			modalConfirm,
		} = this.state;

		const { user } = this.props;

		return (
			<div
				id="jurisdiction-list"
				className="container"
			>
				<div className="row">
					<div className="text-left col-xs-12">
						<h1>{lang.t("Jurisdictions")}</h1>
					</div>
				</div>
				<div
					className="row"
					id="jurisdiction-list-submenu"
				>
					<div className="col-sm-4 col-md-3">
						<SearchField
							placeholder={lang.t("Search Jurisdictions")}
							onSearch={this.onSearchJurisdiction}
							clearable
						/>
					</div>
					<div className="col-sm-3 col-md-3">
						<div className="checkbox">
							<label htmlFor="jurisdictions-includeInactive">
								<input
									type="checkbox"
									name="includeInactive"
									id="jurisdictions-includeInactive"
									checked={!isActive}
									onChange={this.onDisplayInactives}
								/>
								{lang.t("Include Inactive")}
							</label>
						</div>
					</div>
					<div className="col-sm-5 col-md-6 text-right">
						<button
							type="button"
							id="jurisdiction-list-add-btn"
							className="btn btn-primary"
							onClick={() => this.openJurisdictionModal()}
						>
							{`+ ${lang.t("Add Jurisdiction")} `}
						</button>
					</div>
				</div>
				<div
					className="row hidden-xs"
					id="jurisdictions-table-header"
				>
					<div className="col-sm-1">
						<strong>{lang.t("ID")}</strong>
					</div>
					<div className="col-sm-2">
						<strong>{lang.t("Name")}</strong>
					</div>
					<div className="col-sm-2">
						<strong>{lang.t("County")}</strong>
					</div>
					<div className="col-sm-2">
						<strong>{lang.t("State")}</strong>
					</div>
					<div className="col-sm-2">
						<strong>{lang.t("City")}</strong>
					</div>
					<div className="col-sm-3">
						<strong>{lang.t("Active")}</strong>
					</div>
				</div>
				<div id="jurisdictions-table-body">
					<For
						each="item"
						of={jurisdictions}
					>
						<div
							className="row v-align-sm"
							role="button"
							key={`jurisdiction_${item.Id}`}
							tabIndex={0}
							onClick={() => this.openJurisdictionModal(item.Id)}
							onKeyPress={this.onRowKeyPress(item.Id)}
						>
							<div className="col-sm-1 col-sm-push-0 col-xs-4 col-xs-push-8 jurisdiction-id">
								<span className="visible-xs-inline-block">
									{`${lang.t("ID")}:`}
									&nbsp;
								</span>
								{item.Id}
							</div>

							<div className="col-sm-2 col-sm-pull-0 col-xs-8 col-xs-pull-4 jurisdiction-name">
								{item.Name ?? ""}
							</div>

							<div className="col-sm-2 col-xs-12">{item.County ?? ""}</div>

							<div className="col-sm-2 col-xs-12">
								<span className="visible-xs-inline-block">
									{item.City ?? ""},&nbsp;
								</span>
								{item.StateName ?? ""}
							</div>

							<div className="col-sm-2 hidden-xs">{item.City ?? ""}</div>

							<div className="col-sm-3 col-xs-12 jurisdiction-actions">
								<div className="row">
									<div className="col-sm-3 col-xs-12 jurisdiction-toggle">
										<span className="visible-xs-block">
											{lang.t("Active")}
											&nbsp;
										</span>
										<div>
											<Switch
												onClick={this.toggleActiveStatus(item.Id)}
												on={item.IsActive}
												disabled={item.loading}
											/>
										</div>
									</div>
									<div className="col-sm-9 col-xs-12 text-right jurisdiction-buttons">
										<button
											type="button"
											className="btn btn-primary"
											onClick={() => this.openJurisdictionModal(item.Id)}
											disabled={item.loading}
											data-prevent-navigation
										>
											{lang.t("Edit")}
										</button>
										<button
											type="button"
											className="btn btn-primary"
											onClick={this.onDeleteJurisdiction(item.Id)}
											disabled={item.loading}
											data-prevent-navigation
										>
											<Choose>
												<When condition={item.loading}>
													<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
												</When>
												<Otherwise>{lang.t("Delete")}</Otherwise>
											</Choose>
										</button>
									</div>
								</div>
							</div>
						</div>
					</For>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={this.getJurisdictions}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
				<SearchJurisdictionModal
					ref={ref => {
						this._searchJurisdictionModal = ref;
					}}
				/>
				<CreateJurisdictionModal
					user={user}
					show={showJurisdictionModal}
					jurisdictionId={jurisdictionId}
					onSuccess={this.onSuccessCallback}
					onClose={this.closeJurisdictionModal}
				/>
				<JurisdictionConflictModal
					show={showJurisdictionConflictModal}
					modalMessage={conflictModalMessage}
					onCancel={modalCancel}
					onConfirm={modalConfirm}
				/>
			</div>
		);
	}
}

JurisdictionList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
};

JurisdictionList.defaultProps = {
	user: {},
};

export default JurisdictionList;
